import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store';
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice';
import {
	IMAGERY_PROVIDER, API_ERROR_CODES, DESIGN_MODES, MANUAL_ONLY_IMAGERY_PROVIDERS, PREMIUM_IMAGERY_PROVIDERS, ROOF_DESIGN_MODES,
} from '../../constants';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import { useMutation } from 'react-query';
import { switchDesignImagery, switchRoofDesignImagery } from './api';
import { errorResponseHandler } from 'store/api/AxiosError';
import { AxiosError } from 'axios';
import { SVGMap, StyledSelect } from './styles';
import { components, OptionProps, SingleValueProps } from 'react-select';
import { Tooltip } from 'react-tooltip';
import ConfirmationModal from 'components/Basic/Modal/Confirmation';
// import { ReactComponent as PremiumIcon } from 'assets/svg/premium-icon.svg';
import { appToast } from '../Basic/CustomToast';
import { selectIsRecomputing } from 'store/slices/DsmShadingSlice/selectors';
import useStartShadingRecalc from 'hooks/useStartShadingRecalc';
import { isModeDefault } from '../../util';

export type Option = { value: string, label: string, isDisabled?: boolean, isPremiumFetched?: boolean, isPremium?: boolean };
export type Group = { label: string, options: Option[] };

const Message = 'This will reset all your progress on the map. Are you sure you want to continue?';

const IMAGERY_PROVIDER_OPTIONS: Group[] = [{
	label: '— Imagery Provider —',
	options: Object.values(IMAGERY_PROVIDER).map((value, index) => ({
		value,
		label: `Imagery ${String.fromCharCode(65 + index)}`}))
}];

const CustomSingleValue = (props: SingleValueProps<Option>) => {
	return (
		<components.SingleValue {...props}>
			<SVGMap className='imageIcon'
				width="19" height="14" viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path d="M18.8372 12.186V1.60465C18.8372 1.27071 18.5665 1 18.2326 1H1.60465C1.27071 1 1 1.27071 1 
				1.60465V13.3953C1 13.7293 1.27071 14 1.60465 14H6.44186M18.8372 12.186V13.3953C18.8372 13.7293 18.5665 
				14 18.2326 14H6.44186M18.8372 12.186L15.2093 8.55814C14.3023 7.65116 13.2442 7.19767 11.8837 8.55814L6.44186 14"
				stroke="var(--defaultIconColor)" strokeWidth="1.6" />
				<circle cx="5.83739" cy="5.83788" r="1.81395" stroke="var(--defaultIconColor)" strokeWidth="1.6" />
			</SVGMap> <span>{props.data.label}</span>
			{/* {PREMIUM_IMAGERY_PROVIDERS.includes(props.data.value as IMAGERY_PROVIDER) &&
				<PremiumIcon style={{ marginLeft: '6px' }} width={15} height={15} />
			} */}
		</components.SingleValue>
	);
};

const CustomOption = (props: OptionProps<Option>) => {
	const { data, innerRef, innerProps, isFocused, isDisabled, isSelected } = props;

	const optionStyles: React.CSSProperties = {
		backgroundColor: isDisabled
			? 'var(--selectDropdownDisabledBgColor)'
			: isSelected
				? 'var(--selectDropdownActiveColor)'
				: isFocused
					? 'var(--selectDropdownHoverColor)'
					: 'var(--selectDropdownBgColor)',
		color: isDisabled
			? 'var(--selectDropdownDisabledTextColor)'
			: isSelected
				? 'var(--textColor)'
				: 'var(--subHeadingColor)',
		padding: '8px 9px 8px 14px',
		cursor: isDisabled ? 'not-allowed' : 'pointer',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		fontSize: '13px',
		gap: '5px'
	}; 

	return (
		<>
			<div
				ref={innerRef}
				{...innerProps}
				style={optionStyles}
				data-tooltip-content={isDisabled ? 'Imagery isn\'t supported in this mode.' : ''}
				data-tooltip-id={`tooltip-${data.value}`}
			>
				{data.label}
				{/* {!!data?.isPremium && (<IconPremium className='premium-icon'>
					<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 10 10" fill="none">
						<path d="M0.695312 3.84296L4.92245 0.902344L9.1496 3.84296V9.35663H0.695312V3.84296Z" fill="#FEB517"/>
						<path d="M6.62795 8.29579L4.98579 7.25829L3.34896 8.29579L3.78083 6.33294L2.33594 5.02064L4.24468 4.84678L4.98579 
						2.99609L5.72689 4.84117L7.63564 5.01503L6.19075 6.33294L6.62795 8.29579Z" fill="white"/>
					</svg> 
				Premium {!!data?.isPremiumFetched && <svg style={{position:'relative', top: '1px'}} 
						xmlns="http://www.w3.org/2000/svg" width="11" height="8" viewBox="0 0 10 7" fill="none">
						<path d="M1.58594 3.23438L3.9723 5.5071L8.74503 0.734375" 
							stroke="#FEB517" strokeWidth="1.36364" strokeLinecap="round" strokeLinejoin="round"/>
						<path d="M1.58594 3.23438L3.9723 5.5071L8.74503 0.734375" 
							stroke="#FEB517" strokeWidth="1.36364" strokeLinecap="round" strokeLinejoin="round"/>
					</svg>}
				</IconPremium>
				)} */}

			</div>
			{isDisabled && (
				<Tooltip
					id={`tooltip-${data.value}`}
					style={{position: 'fixed'}}
					place="right"
					offset={-100}
					
				/>
			)}
		</>
	);
};

export default function SwitchProvider() {
	const { uuid, panel, roofDataChanging, imagerySource, mode, autoSwitchStatus, billingLogs, designType } = useSelector((state: RootState) => ({
		...state.roofData.data,
		roofDataChanging: state.roofData.configuringNewPanel || state.roofData.loading || state.roofData.switchingImageryProvider
	}));
	const [selectorImageProvidor, setSelectorImageProvidor] = useState(imagerySource);
	const [popupState, setPopupState] = useState<{ open: boolean; message?: string }>({ open: false, message: Message });
	const dispatch = useDispatch<AppDispatch>();
	const allQueriesRunningInBackGround = useQueriesRunningInBackGround();
	const energyQueriesInProgrss = useQueriesRunningInBackGround(['save', `monthlyDetails-${uuid}`, `hourlyEnergy-${uuid}`]);
	const { threeDModelEnabled, hideSatelliteOverlay } = useSelector((state: RootState) => state.toolNewPostions.data);
	const isRecomputing = useSelector(selectIsRecomputing);
	const disableSelect = roofDataChanging || energyQueriesInProgrss || allQueriesRunningInBackGround || threeDModelEnabled
		|| isRecomputing || hideSatelliteOverlay;
	const allowedModes = useSelector((state: RootState) => state.roleState.data.allowedModes);
	const allowManualOnlyImagery = allowedModes.includes(DESIGN_MODES.NEARMAP_NREL_MANUAL) || allowedModes.includes(ROOF_DESIGN_MODES.MANUAL);
	const ImageryProviderOptions: Group[] = useMemo(() => {

		const modeIsNotDefault = !isModeDefault(mode);
		return IMAGERY_PROVIDER_OPTIONS.map((group) => ({
			...group,
			// Google Static / EagleView switch the design to manual, so offer them
			// only when a manual mode is allowed
			options: group.options.filter(o =>
				allowManualOnlyImagery || !MANUAL_ONLY_IMAGERY_PROVIDERS.includes(o.value)
			).map((o) => {
				const isImageryDisabled = o.value === IMAGERY_PROVIDER.SUNROOF && modeIsNotDefault;
			
				// const isPremium = PREMIUM_IMAGERY_PROVIDERS.includes(o.value as IMAGERY_PROVIDER);
				// const isAutoSwitched = autoSwitchStatus?.imageryProvider === o.value;
				// const isBilledAlready = !!billingLogs?.some(log => log.imageryProvider === o.value);
				// // is not autoSwitchStatus means old design then should marked already fetched since not billable.
				// const shouldMarkFetched = !autoSwitchStatus || isAutoSwitched || isBilledAlready;
				return {
					...o,
					isDisabled: isImageryDisabled,
					// isPremiumFetched: isPremium && shouldMarkFetched,
					// isPremium,
				};
			}),
		}));
	}, [allowManualOnlyImagery, mode]);

	const startShadingRecalc = useStartShadingRecalc();

	const switchDesignImageryQuery = useMutation({
		mutationKey: `switch-imagery-${imagerySource}`,
		mutationFn: async () => {
			return await switchDesignImagery({ uuid, panelKey: panel.key, source: selectorImageProvidor });
		},
		onSuccess(shadingStatus) {
			dispatch(changePanel({ uuid, panelKey: panel.key, switchingSource: true, designType: designType, refetchIrradiance: true }));
			startShadingRecalc(shadingStatus);
		},
		onError(error) {
			// eslint-disable-next-line prefer-const
			let { message, error: errCode } = errorResponseHandler(error as AxiosError);
			let errorId = 'switch-imagery' + message;
			if(errCode === API_ERROR_CODES.INVALID_IMAGERY_CHECKOUOUT_SAME_CHECKOUT) {
				errorId = API_ERROR_CODES.INVALID_IMAGERY_CHECKOUOUT_SAME_CHECKOUT;
			}
			else if (errCode === API_ERROR_CODES.INVALID_IMAGERY_CHECKOUT) {
				message = 'The imagery you are trying to checkout isn\'t available for this address';
				errorId = API_ERROR_CODES.INVALID_IMAGERY_CHECKOUT;
			}
			if (appToast.isActive(errorId)) return;
			appToast.error(message, {
				toastId: errorId
			});
		},
		retry(failureCount, error) {
			const { error: parsedErrorMsg } = errorResponseHandler(error as AxiosError);
			return parsedErrorMsg !== API_ERROR_CODES.INVALID_IMAGERY_CHECKOUT && failureCount < 1;
		},
	});

	const switchRoofDesignImageryQuery = useMutation({
		mutationKey: `switch-roof-imagery-${imagerySource}`,
		mutationFn: async () => {
			await switchRoofDesignImagery({ uuid, panelKey: panel.key, source: selectorImageProvidor });
		},
		onSuccess() {
			dispatch(changePanel({ uuid, panelKey: panel.key, switchingSource: true, designType:designType }));
		},
		onError(error) {
			// eslint-disable-next-line prefer-const
			let { message, error: errCode } = errorResponseHandler(error as AxiosError);
			let errorId = 'switch-imagery' + message;
			if(errCode === API_ERROR_CODES.INVALID_IMAGERY_CHECKOUOUT_SAME_CHECKOUT) {
				errorId = API_ERROR_CODES.INVALID_IMAGERY_CHECKOUOUT_SAME_CHECKOUT;
			}
			else if (errCode === API_ERROR_CODES.INVALID_IMAGERY_CHECKOUT) {
				message = 'The imagery you are trying to checkout isn\'t available for this address';
				errorId = API_ERROR_CODES.INVALID_IMAGERY_CHECKOUT;
			}
			if (appToast.isActive(errorId)) return;
			appToast.error(message, {
				toastId: errorId
			});
		},
		retry(failureCount, error) {
			const { error: parsedErrorMsg } = errorResponseHandler(error as AxiosError);
			return parsedErrorMsg !== API_ERROR_CODES.INVALID_IMAGERY_CHECKOUT && failureCount < 1;
		},
	});

	const selectedIMageryProviderOption = useMemo(() =>
		ImageryProviderOptions[0].options.find(o => o.value === imagerySource), [ImageryProviderOptions, imagerySource]);

	const handleChange = (option: Option) => {
		const currentProvider = option.value as ImagerySource;
		if (currentProvider === imagerySource) {
			return;
		}
		let message = Message;
		if (mode !== DESIGN_MODES.NEARMAP_NREL_MANUAL && MANUAL_ONLY_IMAGERY_PROVIDERS.includes(currentProvider)) {
			if (designType === 'SOLAR') {
				message = `This Imagery provider is only supported in NREL(Manual Design) mode. \n
        Selecting this will switch the mode to manual.
        Are you sure you want to proceed?`;
			} else if (designType === 'ROOF') {
				message = `This Imagery provider is only supported in Manual mode. \n
  			Selecting this will switch the mode to manual.
				Are you sure you want to proceed?`;			
			}
		}
		setPopupState({ message, open: true });
		setSelectorImageProvidor(currentProvider);
	};

	const handleConfirmation = async () => {
		if(designType === 'ROOF')
			await switchRoofDesignImageryQuery.mutateAsync();
		else
			await switchDesignImageryQuery.mutateAsync();
		setPopupState({ open: false });
	};

	// const isPremiumImagery = PREMIUM_IMAGERY_PROVIDERS.includes(
	// 	selectorImageProvidor as PremiumImageryProviders
	// );
	// const shouldEnableBilling =
	// 	autoSwitchStatus && autoSwitchStatus.imageryProvider !== selectorImageProvidor && //not autoswitchd
	// 	isPremiumImagery &&
	// 	!billingLogs?.some(l => l.imageryProvider === selectorImageProvidor); 
	// 	not billed before

	return (
		<>
			<StyledSelect
				isDisabled={disableSelect}
				isMulti={false}
				options={ImageryProviderOptions}
				className="imagary-provider-select"
				classNamePrefix="cus-select"
				value={selectedIMageryProviderOption}
				isSearchable={false}
				// menuIsOpen={true} 
				onChange={(option) => handleChange(option as Option)}
				components={{ SingleValue: CustomSingleValue , Option: CustomOption}}
			/>

			<ConfirmationModal 
				open={popupState.open} 
				onCancel={() => setPopupState({open: false})} 
				onConfirm={handleConfirmation} 
				message={popupState.message ?? Message}
				// note={shouldEnableBilling? 
				// 	'You\'re switching to premium imagery, and this will be charged. Click \'Yes\' to confirm.': ''}
			/>
		</>
	);
}
